// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'

// 100 stays comfortably under the server clamp (PageRequest.MaxPageSize = 200), so every
// option here round-trips the paged endpoints unclamped.
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
// Alpida CR-ALPIDA-002, revised (Mario 2026-08-14): rather than force every table to open
// at 100 rows for every tenant (10x the row volume on the 15s-polled hot paths — the
// 2026-08-06 outage load class), the page size is a STICKY PER-USER PREFERENCE. Change it
// on any table and every table opens at that size from then on, in this browser, across
// sessions (localStorage). Staff who want 100 set it once, ever; everyone else stays at
// the lighter default, which is what keeps steady-state DB traffic sane.
// This is still the ONE place the default lives.
export const DEFAULT_PAGE_SIZE = 25

const PAGE_SIZE_PREF_KEY = 'owlrx.pageSize'

/** The user's remembered page size, clamped to the offered options; fallback when unset. */
export function preferredPageSize(fallback: number = DEFAULT_PAGE_SIZE): number {
  try {
    const v = Number(localStorage.getItem(PAGE_SIZE_PREF_KEY))
    return PAGE_SIZE_OPTIONS.includes(v) ? v : fallback
  } catch { return fallback }   // storage blocked (private mode) → fallback, never throw
}

/** Persist the user's page-size choice so every table opens at it from now on. */
export function rememberPageSize(n: number): void {
  try { if (PAGE_SIZE_OPTIONS.includes(n)) localStorage.setItem(PAGE_SIZE_PREF_KEY, String(n)) } catch { /* private mode */ }
}

/**
 * Client-side pagination for an in-memory array. Tables here fetch (and sort/filter) the
 * full result set, then render only one page so the DOM never holds hundreds of heavy rows
 * at once (the cause of the slow render / browser hang). Pair with <Pager> (render it both
 * above and below the table).
 *
 * The page size is selectable (PAGE_SIZE_OPTIONS) and owned here so <Pager> can drive it. `resetKey`
 * — pass a value (e.g. the active tab/filter) that, when it changes, jumps back to page 1.
 */
export function usePaged<T>(rows: T[], initialPageSize: number = DEFAULT_PAGE_SIZE, resetKey?: unknown) {
  // Sticky preference wins over the caller's initial; changing size here updates it for
  // every table (see PAGE_SIZE_PREF_KEY above).
  const [pageSize, setPageSizeRaw] = useState(() => preferredPageSize(initialPageSize))
  const setPageSize = (n: number) => { rememberPageSize(n); setPageSizeRaw(n) }
  const [page, setPage] = useState(1)
  const total = rows.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  useEffect(() => { setPage(1) }, [resetKey, pageSize])
  useEffect(() => { if (page > totalPages) setPage(totalPages) }, [page, totalPages])

  const pageRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize],
  )
  return { page, setPage, pageSize, setPageSize, pageRows, total, totalPages }
}

/**
 * D-PERF-1 P1 — the server-side counterpart to usePaged. Talks to the canonical paged-list contract
 * ({ items, count, total?, page, page_size, total_pages?, next_cursor?, truncated }) and drives the
 * same <Pager> UI. The fetcher is injected so any endpoint can adopt it without a new hook. This is
 * infrastructure; surfaces wire to it in P2/P3.
 *
 * Usage:
 *   const paged = useServerPaged(
 *     ({ page, pageSize }) => api.listPatientsPaged({ page, pageSize }),
 *     10, [debouncedSearch],   // deps: refetch (and reset to page 1) when these change
 *   )
 *   ...paged.rows, <Pager {...paged.pagerProps} />
 */
export interface ServerPage<T> { items: T[]; total?: number; page: number; page_size: number; total_pages?: number; truncated?: boolean }
export function useServerPaged<T>(
  fetcher: (args: { page: number; pageSize: number; signal: AbortSignal }) => Promise<ServerPage<T>>,
  initialPageSize: number = DEFAULT_PAGE_SIZE,
  deps: unknown[] = [],
) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSizeRaw] = useState(() => preferredPageSize(initialPageSize))
  const setPageSize = (n: number) => { rememberPageSize(n); setPageSizeRaw(n) }
  const [rows, setRows] = useState<T[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [totalPages, setTotalPages] = useState(1)
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // reload() refetches THIS page (review L11): a caller that bumped one of `deps` to refresh after a
  // row action was sent back to page 1 and fetched twice — the current page, then page 1.
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick(t => t + 1), [])

  // Reset to page 1 when the caller's deps (search/filter) or page size change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1) }, [pageSize, ...deps])

  useEffect(() => {
    let live = true
    // A superseded fetch is CANCELLED, not just ignored: ignoring it left the server running every
    // keystroke's query to the end (ticket 123qmzr1yyb — 4–9 stacked PA-feed queries per search).
    const ctl = new AbortController()
    setLoading(true); setError(null)
    fetcher({ page, pageSize, signal: ctl.signal })
      .then(d => {
        if (!live) return   // ignore a stale response if a newer fetch superseded it
        // The action emptied the last page (its only row removed): step back to the new last page.
        if ((d.items ?? []).length === 0 && page > 1 && d.total_pages != null && d.total_pages < page) {
          setPage(Math.max(1, d.total_pages)); return
        }
        setRows(d.items ?? [])
        setTotal(typeof d.total === 'number' ? d.total : null)
        setTotalPages(d.total_pages ?? (d.truncated ? page + 1 : page))
        setTruncated(!!d.truncated)
      })
      .catch(e => { if (live) setError(e) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false; ctl.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, tick, ...deps])

  return {
    rows, page, setPage, pageSize, setPageSize, total, totalPages, truncated, loading, error, reload,
    // spread straight into <Pager {...pagerProps} />
    pagerProps: {
      page, totalPages, total: total ?? rows.length, pageSize,
      onPage: setPage, onPageSize: setPageSize,
    },
  }
}
const btn: CSSProperties = {
  border: '1px solid #d9e0e8', background: '#fff', borderRadius: 7,
  padding: '5px 12px', fontSize: 13, fontWeight: 600, color: '#33404f', cursor: 'pointer',
}
const disabledBtn: CSSProperties = { opacity: 0.4, cursor: 'default' }
const sel: CSSProperties = {
  border: '1px solid #d9e0e8', borderRadius: 7, padding: '4px 8px', fontSize: 13, color: '#33404f', cursor: 'pointer',
}

/**
 * Prev / page-indicator / Next + a "x-y of N" range, and (when onPageSize is provided and
 * showPageSize is true) a page-size dropdown. Renders nothing when empty. Render one above
 * the table (with the size dropdown) and one below (without).
 */
export function Pager({
  page, totalPages, total, pageSize, onPage, onPageSize, showPageSize = true, label = 'rows',
}: {
  page: number; totalPages: number; total: number; pageSize: number
  onPage: (p: number) => void
  onPageSize?: (n: number) => void
  showPageSize?: boolean
  label?: string
}) {
  if (total === 0) return null
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  return (
    <div role="navigation" aria-label="Pagination"
         style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end',
                  flexWrap: 'wrap', padding: '8px 2px', fontSize: 13, color: '#5d6b7b' }}>
      {showPageSize && onPageSize && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Show
          <select value={pageSize} aria-label="Rows per page" style={sel}
                  onChange={e => onPageSize(Number(e.target.value))}>
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          per page
        </label>
      )}
      <span>{from.toLocaleString()}-{to.toLocaleString()} of {total.toLocaleString()} {label}</span>
      <button style={{ ...btn, ...(page <= 1 ? disabledBtn : {}) }} disabled={page <= 1}
              onClick={() => onPage(Math.max(1, page - 1))} aria-label="Previous page">Prev</button>
      <span aria-live="polite">Page {page} / {totalPages}</span>
      <button style={{ ...btn, ...(page >= totalPages ? disabledBtn : {}) }} disabled={page >= totalPages}
              onClick={() => onPage(Math.min(totalPages, page + 1))} aria-label="Next page">Next</button>
    </div>
  )
}
